"use client";

import { H2, H4 } from "@/components/ui/heading";
import { useTranslations } from "next-intl";

import visual from "../../_assets/valuation-tool-upsell-visual.png";
import Image from "next/image";
import { valuesWithAppName } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { useMemo } from "react";
import { useAtom } from "jotai";
import { valuationToolAtom } from "@/app/(marketing)/(tools)/free-business-valuation/_lib/state";
import { cleanupBusinessTypeName } from "@/app/(marketing)/(tools)/free-business-valuation/_lib/utils";
import { Icon } from "@/components/ui/icon";
import { cn } from "@/lib/utils";
import { useCurrentUser } from "@/hooks/use-current-user";
import { api } from "@/lib/api-client";
import { useRouter } from "next/navigation";
import { buildStepUrl } from "@repo/utils";
import { toast } from "sonner";
import { NewListingButtonProvider } from "@/app/(saas)/(app)/(common)/my-listings/_components/providers/new-listing-button-provider";
import { NewListingButton } from "@/app/(saas)/(app)/(common)/my-listings/_components/new-listing-button";
import { useErrorFormatter } from "@/hooks/use-error-formatter";
import { AuthGuard } from "@/components/guards/auth-guard";

export type UpsellBlockVariant = "primary" | "secondary";

export function UpsellBlock({
  variant = "primary",
}: {
  variant?: UpsellBlockVariant;
}) {
  const t = useTranslations();

  const { user, displayAuthDialog } = useCurrentUser();

  const router = useRouter();

  const [valuationToolData] = useAtom(valuationToolAtom);

  const claimMutation = api.listings.valuation.claim.useMutation();

  const { displayErrorToast } = useErrorFormatter();

  async function claimAndEdit() {
    if (!valuationToolData.listing?.valuationSessionId) {
      toast.error(t("errors.valuationTool.invalidListing"));
      return;
    }

    try {
      await claimMutation.mutateAsync({
        vid: valuationToolData.listing.valuationSessionId,
      });
    } catch (err) {
      displayErrorToast(err);
      return;
    }

    router.push(buildStepUrl(valuationToolData.listing, "CONFIRM_LOCATION"));
  }

  async function handlePrimaryCtaClick() {
    if (user) {
      if (!valuationToolData.listing) {
        router.push("/my-listings"); // TODO: maybe make it nicer
        return;
      }

      router.push(buildStepUrl(valuationToolData.listing, "CONFIRM_LOCATION"));
      return;
    }

    displayAuthDialog({
      intent: "seller",
      skipSegmentation: true,
      defaultValues: {
        email: valuationToolData.email,
      },
      onLoggedIn: async (user) => {
        if (!user) {
          return;
        }

        await claimAndEdit();
      },
    });
  }

  const title = useMemo(() => {
    switch (variant) {
      case "primary":
        return t("tools.valuation.upsell.title", {
          businessType: cleanupBusinessTypeName(
            valuationToolData.businessType?.name ?? "",
          ),
        });
      case "secondary":
        return t("tools.valuation.upsell.titleSecondary");
    }
  }, [variant]);

  const subtitle = useMemo(() => {
    switch (variant) {
      case "primary":
        return t("tools.valuation.upsell.subtitle", valuesWithAppName(t, {}));
      case "secondary":
        return t(
          "tools.valuation.upsell.subtitleSecondary",
          valuesWithAppName(t, {}),
        );
    }
  }, [variant]);

  return (
    <section className="grid grid-cols-1 gap-4 md:grid-cols-[minmax(0,1fr)_20rem] md:items-stretch">
      <div
        className={cn("rounded-lg flex flex-col gap-3", {
          "border p-6 md:p-7": variant === "primary",
          "border border-border/70 bg-gradient-to-br from-theme-blue-soft to-[theme-green-soft] p-6 md:p-8":
            variant === "secondary",
        })}
      >
        <div className="flex flex-col gap-1">
          <H2
            className={cn({
              "text-2xl md:text-2xl lg:text-2xl": variant === "primary",
            })}
          >
            {title}
          </H2>

          <H4
            className={cn({
              "text-xl md:text-xl lg:text-xl": variant === "primary",
            })}
          >
            {subtitle}
          </H4>
        </div>

        <p
          className={cn({
            "text-sm": variant === "primary",
            "text-lg": variant === "secondary",
          })}
        >
          {t("tools.valuation.upsell.description", valuesWithAppName(t, {}))}
        </p>

        {variant === "primary" && (
          <Button onClick={handlePrimaryCtaClick}>
            <Icon icon="Plus" />
            {t("tools.valuation.upsell.cta")}
          </Button>
        )}

        {user && variant === "secondary" && (
          <NewListingButtonProvider>
            <NewListingButton variant="outline">
              <Icon icon="Plus" />
              {t("tools.valuation.upsell.cta")}
            </NewListingButton>
          </NewListingButtonProvider>
        )}

        {!user && variant === "secondary" && (
          <AuthGuard>
            <Button variant="outline">
              <Icon icon="Plus" />
              {t("tools.valuation.upsell.cta")}
            </Button>
          </AuthGuard>
        )}
      </div>

      <div className="h-[220px] overflow-hidden rounded-lg md:h-auto">
        <div className="relative h-full">
          <Image
            src={visual.src}
            alt=""
            fill
            className="h-full w-full object-cover"
          />
        </div>
      </div>
    </section>
  );
}
